import { emptyState, type Game, type Group, type State } from "./store.js";

/**
 * Export and import (PRD FR-8).
 *
 * The format is the app's own state plus a version, so an export is readable
 * and an import needs no translation. FR-8.3 also requires accepting the
 * prototype's shape, which nested games inside their group rather than keeping
 * them in one list.
 */

export const EXPORT_VERSION = 1;

export interface ExportFile {
  version: number;
  exportedAt: number;
  groups: Group[];
  games: Game[];
}

export function exportState(state: State, now: number): ExportFile {
  return {
    version: EXPORT_VERSION,
    exportedAt: now,
    groups: state.groups,
    // The game in progress goes too: an export taken mid-evening should not
    // quietly omit the evening.
    games: state.game ? [...state.history, state.game] : state.history,
  };
}

interface LegacyGroup {
  id?: unknown;
  name?: unknown;
  players?: unknown;
  games?: unknown;
}

function asPlayers(value: unknown): Group["players"] {
  if (!Array.isArray(value)) return [];
  return value
    .map((p): Group["players"][number] | null => {
      if (typeof p === "string") return { id: p, name: p };
      if (p && typeof p === "object") {
        const rec = p as Record<string, unknown>;
        const id = typeof rec["id"] === "string" ? rec["id"] : null;
        const name =
          typeof rec["name"] === "string"
            ? rec["name"]
            : typeof rec["displayName"] === "string"
              ? rec["displayName"]
              : id;
        if (id && name) return { id, name };
      }
      return null;
    })
    .filter((p): p is Group["players"][number] => p !== null);
}

function asGames(value: unknown, groupId: string): Game[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((g): Game | null => {
      if (!g || typeof g !== "object") return null;
      const rec = g as Record<string, unknown>;
      const id = typeof rec["id"] === "string" ? rec["id"] : null;
      const order = Array.isArray(rec["order"])
        ? rec["order"].filter((x): x is string => typeof x === "string")
        : [];
      const turns = Array.isArray(rec["turns"]) ? (rec["turns"] as Game["turns"]) : [];
      if (!id || order.length === 0) return null;
      return {
        id,
        groupId: typeof rec["groupId"] === "string" ? rec["groupId"] : groupId,
        order,
        turns,
        startedAt: typeof rec["startedAt"] === "number" ? rec["startedAt"] : 0,
        ...(typeof rec["finishedAt"] === "number" ? { finishedAt: rec["finishedAt"] } : {}),
      };
    })
    .filter((g): g is Game => g !== null);
}

export interface ImportResult {
  state: State;
  groupsAdded: number;
  groupsReplaced: number;
  gamesImported: number;
}

/**
 * FR-8.2: merge by group id -- a matching id is replaced, an unknown one is
 * appended. Replacing rather than merging turn-by-turn is deliberate: two
 * divergent copies of one group cannot be reconciled without inventing a rule
 * about which turn wins, and silently picking one would corrupt a score.
 */
export function importInto(state: State, raw: unknown): ImportResult {
  if (!raw || typeof raw !== "object") {
    return { state, groupsAdded: 0, groupsReplaced: 0, gamesImported: 0 };
  }
  const file = raw as Record<string, unknown>;
  const incomingGroups = Array.isArray(file["groups"]) ? (file["groups"] as LegacyGroup[]) : [];
  const flatGames = asGames(file["games"], "");

  const groups: Group[] = [];
  const games: Game[] = [...flatGames];

  for (const g of incomingGroups) {
    const id = typeof g.id === "string" ? g.id : null;
    const name = typeof g.name === "string" ? g.name : null;
    if (!id || !name) continue;
    groups.push({ id, name, players: asPlayers(g.players) });
    // FR-8.3: the prototype nested a group's games inside it.
    games.push(...asGames(g.games, id));
  }

  const known = new Set(state.groups.map((g) => g.id));
  const incomingIds = new Set(groups.map((g) => g.id));
  let added = 0;
  let replaced = 0;
  for (const g of groups) {
    if (known.has(g.id)) replaced++;
    else added++;
  }

  const mergedGroups = [
    ...state.groups.map((g) => groups.find((n) => n.id === g.id) ?? g),
    ...groups.filter((g) => !known.has(g.id)),
  ];

  // Drop the local history of any group being replaced, then take the file's.
  const keptHistory = state.history.filter((g) => !incomingIds.has(g.groupId));
  const importedHistory = games.filter((g) => g.finishedAt !== undefined);

  return {
    state: {
      ...state,
      groups: mergedGroups,
      history: [...importedHistory, ...keptHistory].sort(
        (a, b) => (b.finishedAt ?? b.startedAt) - (a.finishedAt ?? a.startedAt),
      ),
    },
    groupsAdded: added,
    groupsReplaced: replaced,
    gamesImported: importedHistory.length,
  };
}

/** A fresh state, for callers that want to replace rather than merge. */
export const blankState = emptyState;
